// Which panel or dialog is open. Each one lives in its own file.

import { lazy, Suspense, useEffect, useState } from "react";
import { codeShows, get, set, sharedShows, useStore } from "../state/store";
import { Recover } from "./Recover";
import { PhoneBar } from "../phone/PhoneBar";
import { X } from "lucide-react";

const Gallery = lazy(() => import("../panels/Gallery"));
const ThemeStudio = lazy(() => import("../panels/ThemeStudio"));
const Connect = lazy(() => import("../panels/connect/Hub"));
const DataPanel = lazy(() => import("../panels/DataPanel"));
const SourceEditor = lazy(() => import("../panels/SourceEditor"));
const Share = lazy(() => import("../panels/Share"));
const Settings = lazy(() => import("../panels/Settings"));
const Shortcuts = lazy(() => import("../panels/Shortcuts"));
const NewBoard = lazy(() => import("../boards/Picker"));
const ConnectionsView = lazy(() => import("../panels/ConnectionsView"));
const Instructions = lazy(() => import("../panels/Instructions"));
// Import (S3): docs, notes and chats brought over as pages, its parsers loaded only when it opens
const ImportPanel = lazy(() => import("../import/ImportDialog"));
// help for the board in view (Features 4, onboarding part 5): its words in shared/help.ts
const HelpPanel = lazy(() => import("../help/HelpPanel"));
// all boards opens with a zoom that cannot wait: its code is loaded ahead, and
// once it is here it is drawn straight away, without the pause a lazy
// component takes to show
let overview: typeof import("../panels/Overview") | null = null;
const loadOverview = () =>
  import("../panels/Overview").then((m) => (overview = m));
const Inspector = lazy(() => import("../panels/Inspector"));
const InkPrefs = lazy(() =>
  import("../panels/Inspector").then((m) => ({ default: m.InkPrefsPanel })),
);

/**
 * Every board on one screen, tiled like windows. Not in full screen, which
 * shows one board on purpose, and never over the two dialogs outside the one
 * panel place, the code editor and a shared theme waiting to be added: each
 * keeps the keyboard, and its Esc would close the overview with it. Only while
 * one can show, so neither ever holds it shut for good. A panel's own dialog
 * (the shortcuts, Share, Settings) is in the panel place, and all boards takes
 * its place, unless someone typed words in it that are not saved yet: the
 * store's guard keeps those (store.ts), and nothing here changes.
 */
export function openOverview() {
  const s = get();
  if (s.presenting || !s.boards.length || codeShows(s) || sharedShows(s))
    return;
  set({
    panel: "boards",
    panelArg: null,
    themePicker: false,
    themePreview: null,
  });
}

/**
 * All boards, on any screen: a phone's column of widgets has it too. It is one
 * component from the moment its code is here, so a re-render never swaps an
 * open overview for a fresh one; asked for sooner, it comes when its code does.
 */
export function OverviewHost() {
  const open = useStore((s) => s.panel === "boards");
  const mobile = useStore((s) => s.mobile);
  const [mod, setMod] = useState(overview);
  // loaded, and its scenes drawn, before anyone asks for it
  useEffect(() => {
    if (mod) return;
    let live = true;
    const t = setTimeout(
      () => void loadOverview().then((m) => live && setMod(m)),
      open ? 0 : 1500,
    );
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [mod, open]);
  useEffect(() => {
    if (mod) mod.warmOverview();
  }, [mod]);
  if (!open) return null;
  // its code still on the way: a phone shows its head and bar at once (the board behind, dimmed), never the old screen (Fixes 10)
  if (!mod)
    return mobile ? (
      <div className="ov" role="status" aria-label="Opening All boards">
        <div className="ov-phone-head island">
          <b>All boards</b>
          <button className="icon-btn ov-phone-x" aria-label="Close All boards" onClick={() => set({ panel: null, panelArg: null })}>
            <X size={18} />
          </button>
        </div>
        <PhoneBar inAllBoards />
      </div>
    ) : null;
  return <mod.default />;
}

export function Panels() {
  const panel = useStore((s) => s.panel);
  const selection = useStore((s) => s.selection.length);
  // one widget shows its settings when asked for them; a drawing, or several things at once, as soon as chosen
  const settings = useStore((s) => s.inspect || s.selection.length > 1 || (s.selection.length === 1 && s.items[s.selection[0]]?.kind !== "widget"));
  const editing = useStore((s) => s.editing);
  const drawing = useStore((s) => s.tool !== "select");
  const guest = useStore((s) => !!s.guest);
  // a panel that cannot draw shows the Reload card in its place, and takes nothing else with it
  return (
    <Recover key={panel ?? "none"}>
      <Suspense fallback={null}>
        {/* a board shared with this person: only its widgets' own settings, never the owner's panels (sharing) */}
        {!guest && panel === "gallery" && <Gallery />}
        {!guest && panel === "theme" && <ThemeStudio />}
        {!guest && panel === "connect" && <Connect />}
        {!guest && panel === "data" && <DataPanel />}
        {!guest && panel === "source" && <SourceEditor />}
        {!guest && panel === "share" && <Share />}
        {!guest && panel === "settings" && <Settings />}
        {panel === "shortcuts" && <Shortcuts />}
        {!guest && panel === "newboard" && <NewBoard />}
        {!guest && panel === "connections" && <ConnectionsView />}
        {!guest && panel === "instructions" && <Instructions />}
        {!guest && panel === "import" && <ImportPanel />}
        {!guest && panel === "help" && <HelpPanel />}
        {!panel && selection > 0 && settings && !editing && <Inspector />}
        {!panel && selection === 0 && drawing && <InkPrefs />}
      </Suspense>
    </Recover>
  );
}
